/**
 * Ürün Reçetesi (BOM) + Ayak Listesi — eski `products_single.php` "Ürün Reçetesi"
 * ve "Ayak Listesi" bloklarının karşılığı.
 *
 * Eski sistemin düzeltilen hataları:
 *  · G-10: `scripts/getrecipes.php` her role `unitprice` ve `elementprice`
 *    döndürüyordu — reçeteyi görebilen herkes alış fiyatlarını görüyordu.
 *    Burada reçete satırında fiyat ALANI YOKTUR; fiyat yalnızca `seeCost`
 *    yetkisi varsa ve `components/{id}/private/pricing` üzerinden AYRI sorguyla
 *    gösterilir.
 *  · Reçete satırı düzenlenemiyordu (sil + yeniden ekle gerekiyordu). Artık
 *    `saveRecipeLine` aynı satırı günceller.
 *  · Koli / ayak / bileşen ayrımı `nonstock` + `islegdescription` + "component_id=0
 *    INNER JOIN ile elenir" gibi gizli kurallara dayanıyordu. Artık açık `type`
 *    alanı: component | packaging | leg.
 *  · `production` rolü reçeteyi düzenleyebiliyordu (G-11). Artık salt okur.
 */

import { Fragment, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { where, orderBy } from "firebase/firestore";
import { PageHeader, Modal, Spinner, EmptyState, useToast } from "../components";
import { useCollection, useDoc, useCallableAction } from "../hooks";
import type { CallableActionState } from "../hooks/useCallableAction";
import { useCan } from "../lib/auth";
import { tl, n } from "../lib/format";
import { DecimalInput, ErrorNote, Field } from "../components";
import type { ComponentCategory, ComponentDoc, ComponentPricing } from "../lib/types";
import { parseDecimal, formatDecimalInput } from "../lib/decimal";
import type { ProductDoc } from "./Products";

export type RecipeLineType = "component" | "packaging" | "leg";

export interface RecipeLine {
  id: string;
  type: RecipeLineType;
  /** `leg` satırlarında "" — ayak tarifi serbest metindir, parçası yoktur. */
  componentId: string;
  /** 1 adet mamul için gereken miktar. Ondalık olabilir (0,5 m² panel). */
  quantity: number;
  explanation?: string | null;
  order?: number;
  /**
   * Parça bilgisinin kopyası. FİYAT ALANI YASAKTIR — Rules `unitPrice`,
   * `elementPrice` ve `cost` anahtarlarını reddeder.
   */
  snapshot?: {
    name?: string;
    unit?: string;
    categoryId?: string;
    categoryName?: string;
    /** Göçten gelen ad — `categoryName` ile aynı anlam. */
    category?: string;
    /**
     * ★ ÖLÇÜLER `dimensions` ALTINDADIR, DÜZ DEĞİL.
     *
     * Anlık görüntüyü yazan tek yer (`catalog/recipe.ts:175-181`)
     * `snapshot.dimensions = { length, width, height }` yazar. Düz
     * `snapshot.height/width/length` diye bir alan HİÇ yazılmaz — canlı
     * ölçüm: 279 paket satırının 279'unda `dimensions.*` dolu, düz adların
     * üçü de 0/279. Paketleme ekranındaki "Ölçü Y×G×U" sütunu bu yüzden
     * HER satırda "—" gösteriyordu.
     *
     * Düz adlar tipte KALIYOR: eski/göç kayıtları taşıyor olabilir ve
     * okuyucu ikisini de denemeli.
     */
    dimensions?: {
      height?: number;
      width?: number;
      length?: number;
    };
    height?: number;
    width?: number;
    length?: number;
    deci?: number;
    missing?: boolean;
  };
  deleted?: boolean;
}

export interface SaveRecipeLineInput {
  productId: string;
  /** Boşsa yeni satır açılır. */
  lineId?: string;
  type: RecipeLineType;
  componentId: string;
  quantity: number;
  explanation: string;
  order: number;
}

export interface SaveRecipeLineOutput {
  lineId: string;
}

export interface DeleteRecipeLineInput {
  productId: string;
  lineId: string;
}


const INPUT =
  "w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 read-only:bg-slate-100";

/**
 * ★ REÇETE ÜÇ ALT KOLEKSİYONA BÖLÜNDÜ.
 *
 * Eski `erp_product_recipes` tek tabloydu ve ayak/koli ayrımı bir INNER JOIN
 * yan etkisiyle yapılıyordu; koli satırları sahte `componentId = 0` taşıyordu.
 * Ayrım artık KOLEKSİYON SINIRI — `where("type", ...)` filtresine gerek yok.
 *
 * Canlı veriden doğrulandı: ayak açıklaması satırları (islegdescription=1,
 * 1.076 kayıt) hiçbir parçaya bağlı DEĞİL, serbest metin taşıyor. Bu yüzden
 * `legs` ayrı bir koleksiyon olmak zorunda.
 */
export function recipePath(productId: string) {
  return `products/${productId}/recipes`;
}

/** Koli satırları — desinin tek kaynağı (kategori 2 / AMBALAJ). */
export function packagingPath(productId: string) {
  return `products/${productId}/packaging`;
}

/** Ayak açıklamaları — serbest metin, componentId YOK. */
export function legsPath(productId: string) {
  return `products/${productId}/legs`;
}

// ═══════════════════════════════════════════════════════════════════════════
// SATIR FİYATI — YALNIZCA seeCost, AYRI SORGU
// Reçete satırında fiyat denormalize edilmez; bu bileşen yalnızca yetkili
// kullanıcı için render edilir, dolayısıyla yetkisiz kullanıcıda abonelik
// hiç kurulmaz (Rules zaten reddederdi).
// ═══════════════════════════════════════════════════════════════════════════

function LineCost({ componentId, quantity }: { componentId: string; quantity: number }) {
  const { data, loading, error } = useDoc<ComponentPricing>(
    `components/${componentId}/private/pricing`,
  );

  if (loading) return <span className="text-xs text-slate-600">…</span>;
  if (error) return <span className="text-xs text-red-600">{error}</span>;
  if (!data) {
    return (
      <span className="text-xs text-amber-600" title="Alış fiyatı girilmemiş — maliyet eksik.">
        fiyat yok
      </span>
    );
  }

  // Görüntüleme amaçlı satır tutarı. Resmî maliyet HER ZAMAN sunucudaki
  // `products/{id}/private/cost` dokümanıdır; buradaki değer yalnızca ipucudur.
  const lineKurus = Math.round(data.unitPrice * (Number.isFinite(quantity) ? quantity : 0));
  return (
    /* ★ Dar ekranda birim fiyat ile satır tutarı alt alta yazılır; yan yana
       dizilim sütunu tablette/telefonda gereksiz genişletiyordu. */
    <span className="tabular-nums">
      <span className="block text-slate-500 lg:inline">{tl(data.unitPrice)}</span>
      <span className="mx-1 hidden text-slate-300 lg:inline">×</span>
      <strong className="block text-slate-800 lg:inline">{tl(lineKurus)}</strong>
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ROTA SAYFASI
// ═══════════════════════════════════════════════════════════════════════════

export default function RecipePage() {
  const { productId: routeId } = useParams<{ productId: string }>();
  const productId = routeId ?? "";
  const { data: product, loading } = useDoc<ProductDoc>(`products/${productId}`);

  return (
    <PageHeader
      title={product ? `Reçete — ${product.name}` : "Reçete"}
    >
      {loading ? <Spinner /> : null}
      {!loading && !product ? (
        <EmptyState title="Ürün bulunamadı" description="Bağlantı geçersiz olabilir." />
      ) : null}
      {product ? (
        <>
          <Link to={`/products/${productId}`} className="mb-4 inline-block text-sm text-sky-700 hover:underline">
            ← Ürün kartına dön
          </Link>
          <RecipeBlock productId={productId} />
          <div className="mt-8">
            <LegBlock productId={productId} />
          </div>
        </>
      ) : null}
    </PageHeader>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// REÇETE BLOĞU — ürün kartına da gömülür
// ═══════════════════════════════════════════════════════════════════════════

export function RecipeBlock({ productId }: { productId: string }) {
  const can = useCan();
  const toast = useToast();
  const readOnly = !can.isStaff;

  const { data: lines, loading, error } = useCollection<RecipeLine>(recipePath(productId), [
    where("deleted", "==", false),
    orderBy("order"),
  ]);

  const { data: categories } = useCollection<ComponentCategory>("componentCategories", [
    where("deleted", "==", false),
    orderBy("order"),
  ]);

  const { data: components } = useCollection<ComponentDoc>("components", [
    where("deleted", "==", false),
    where("active", "==", true),
    orderBy("nameLower"),
  ]);

  const save = useCallableAction<SaveRecipeLineInput, SaveRecipeLineOutput>("saveRecipeLine");
  const remove = useCallableAction<DeleteRecipeLineInput, { ok: boolean }>("deleteRecipeLine");

  const [editing, setEditing] = useState<RecipeLine | "new" | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<RecipeLine | null>(null);

  // ★ `type` ALANINA GÖRE SÜZME YOK.
  //
  // Reçete üç AYRI alt koleksiyona bölündüğünde (`recipes` / `packaging` /
  // `legs`) `type` alanı gereksizleşti ve yazılmadı. Bu satır hâlâ
  // `type === "component"` süzüyordu; alan olmadığı için HER ürünün TÜM reçete
  // satırları eleniyor ve ekran "Reçete tanımlı değil" diyordu — 387 ürünün
  // hepsinde. Ayrım artık koleksiyon sınırıdır; `recipes` zaten yalnızca
  // bileşen satırı taşır.
  const componentLines = lines;

  /** Eski liste kategoriye göre gruplanıp başlık satırı basıyordu; aynısı. */
  const grouped = useMemo(() => {
    const map = new Map<string, RecipeLine[]>();
    for (const l of componentLines) {
      // Göç `snapshot.category` yazdı, tasarım `categoryName` bekliyordu:
      // ikisini de kabul ediyoruz, yoksa TÜM satırlar "Kategorisiz"e düşüyor.
      const key = l.snapshot?.categoryName ?? l.snapshot?.category ?? "Kategorisiz";
      const arr = map.get(key);
      if (arr) arr.push(l);
      else map.set(key, [l]);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0], "tr"));
  }, [componentLines]);

  const missingCount = componentLines.filter((l) => l.snapshot?.missing === true).length;

  // Bileşen · Miktar · Birim · Açıklama (+ tutar, + işlem)
  const colCount = 4 + (can.seeCost ? 1 : 0) + (readOnly ? 0 : 1);
  const lastLine = componentLines[componentLines.length - 1];

  async function doDelete(line: RecipeLine) {
    const res = await remove.run({ productId, lineId: line.id });
    if (res) {
      toast.success("Reçete satırı silindi.");
      setConfirmDelete(null);
    }
  }

  return (
    <section>
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-slate-800">Ürün Reçetesi</h3>
          <p className="text-xs text-slate-500">
            Stoğa tabi gerçek bileşenler. Üretim tamamlanınca bu satırlar stoktan düşer.
          </p>
        </div>
        {!readOnly ? (
          <button
            type="button"
            onClick={() => setEditing("new")}
            className="rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800"
          >
            Bileşen Ekle
          </button>
        ) : null}
      </header>

      {readOnly ? (
        <p className="mb-3 rounded border border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Üretim rolünde reçete <strong>salt okunurdur</strong>. Değişiklik için ofise başvurun.
        </p>
      ) : null}

      {missingCount > 0 ? (
        <div className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
          {n(missingCount)} satırın parçası katalogda bulunamıyor. Bu satırlar maliyete
          katılmaz; parça pasife alınmış veya silinmiş olabilir.
        </div>
      ) : null}

      {error ? <ErrorNote error={error} /> : null}
      {remove.error ? <ErrorNote error={remove.error} /> : null}
      {loading ? <Spinner /> : null}

      {!loading && componentLines.length === 0 ? (
        <EmptyState
          title="Reçete tanımlı değil"
          description="Reçetesi olmayan üründe maliyet hesaplanamaz ve üretimde stok düşmez."
        />
      ) : null}

      {componentLines.length > 0 ? (
        <div className="overflow-x-auto rounded border border-slate-200">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs uppercase tracking-wide text-slate-600">
              <tr>
                {/* ★ TABLET/TELEFON: satır telefonda 598px yer istiyordu (343px
                    kap). Birim ve açıklama dar ekranda gizlenip bileşen adının
                    altına iner; masaüstü (lg+) düzeni değişmez. */}
                <th className="px-3 py-2">Bileşen</th>
                <th className="px-3 py-2 text-right">Miktar</th>
                <th className="hidden px-3 py-2 sm:table-cell">Birim</th>
                <th className="hidden px-3 py-2 lg:table-cell">Açıklama</th>
                {can.seeCost ? <th className="px-3 py-2 text-right">Birim / Satır Tutarı</th> : null}
                {!readOnly ? <th className="px-3 py-2 text-right">İşlem</th> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {grouped.map(([category, group]) => (
                <Fragment key={category}>
                  <tr className="bg-slate-50">
                    <td
                      colSpan={colCount}
                      className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"
                    >
                      {category}
                    </td>
                  </tr>
                  {group.map((l) => (
                    <tr key={l.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <span className={l.snapshot?.missing ? "text-red-700 line-through" : "text-slate-800"}>
                          {l.snapshot?.name ?? l.componentId}
                        </span>
                        {l.snapshot?.missing ? (
                          <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-700">
                            parça yok
                          </span>
                        ) : null}
                        {/* Dar ekranda gizlenen sütunlar burada görünür. */}
                        <span className="mt-1 block text-xs text-slate-600 sm:hidden">
                          Birim: {l.snapshot?.unit ?? "—"}
                        </span>
                        {l.explanation ? (
                          <span className="mt-0.5 block text-xs text-slate-600 lg:hidden">
                            {l.explanation}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{n(l.quantity)}</td>
                      <td className="hidden px-3 py-2 text-slate-600 sm:table-cell">
                        {l.snapshot?.unit ?? "—"}
                      </td>
                      <td className="hidden px-3 py-2 text-slate-500 lg:table-cell">
                        {l.explanation || "—"}
                      </td>
                      {can.seeCost ? (
                        <td className="px-3 py-2 text-right">
                          {l.componentId ? (
                            <LineCost componentId={l.componentId} quantity={l.quantity} />
                          ) : (
                            "—"
                          )}
                        </td>
                      ) : null}
                      {!readOnly ? (
                        /* Eylemler dar ekranda alt alta ve 44px dokunma hedefi. */
                        <td className="px-3 py-2 text-right lg:whitespace-nowrap">
                          <div className="flex flex-col items-stretch gap-1 lg:flex-row lg:items-center lg:justify-end lg:gap-0">
                            <button
                              type="button"
                              className="inline-flex min-h-[44px] items-center justify-center rounded border border-slate-300 px-3 py-1 text-xs hover:bg-slate-100 lg:min-h-0 lg:px-2"
                              onClick={() => setEditing(l)}
                            >
                              Düzenle
                            </button>
                            <button
                              type="button"
                              className="inline-flex min-h-[44px] items-center justify-center rounded border border-red-300 px-3 py-1 text-xs text-red-700 hover:bg-red-50 lg:ml-1 lg:min-h-0 lg:px-2"
                              onClick={() => setConfirmDelete(l)}
                            >
                              Çıkar
                            </button>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {can.seeCost ? (
        <p className="mt-2 text-xs text-slate-600">
          Buradaki satır tutarları bilgi amaçlıdır. Bağlayıcı maliyet ve kâr rakamı sunucuda
          hesaplanır ve ürün kartındaki Maliyet/Kâr bloğunda gösterilir.
        </p>
      ) : null}

      {editing ? (
        <RecipeLineEditor
          productId={productId}
          line={editing === "new" ? null : editing}
          categories={categories}
          components={components}
          nextOrder={(lastLine?.order ?? 0) + 10}
          save={save}
          onClose={() => setEditing(null)}
          onSaved={() => {
            toast.success("Reçete satırı kaydedildi.");
            setEditing(null);
          }}
        />
      ) : null}

      {confirmDelete ? (
        <Modal open onClose={() => setConfirmDelete(null)} title="Reçete satırını çıkar">
          <p className="text-sm text-slate-700">
            <strong>{confirmDelete.snapshot?.name ?? confirmDelete.componentId}</strong> satırı
            reçeteden çıkarılacak. Bu işlem ürünün maliyetini ve üretimdeki stok düşümünü değiştirir.
          </p>
          <ErrorNote error={remove.error} />
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="rounded border border-slate-300 px-4 py-2 text-sm"
              onClick={() => setConfirmDelete(null)}
            >
              Vazgeç
            </button>
            <button
              type="button"
              disabled={remove.loading}
              className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              onClick={() => void doDelete(confirmDelete)}
            >
              {remove.loading ? "Siliniyor…" : "Çıkar"}
            </button>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// REÇETE SATIRI DÜZENLEME — Kategori → Parça → Miktar
// ═══════════════════════════════════════════════════════════════════════════

function RecipeLineEditor({
  productId,
  line,
  categories,
  components,
  nextOrder,
  save,
  onClose,
  onSaved,
}: {
  productId: string;
  line: RecipeLine | null;
  categories: ComponentCategory[];
  components: ComponentDoc[];
  nextOrder: number;
  save: CallableActionState<SaveRecipeLineInput, SaveRecipeLineOutput>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [categoryId, setCategoryId] = useState(line?.snapshot?.categoryId ?? "");
  const [componentId, setComponentId] = useState(line?.componentId ?? "");
  const [quantity, setQuantity] = useState(formatDecimalInput(line?.quantity));
  const [explanation, setExplanation] = useState(line?.explanation ?? "");
  const [localError, setLocalError] = useState<string | null>(null);

  // Eski `selectcomponentcategories.php` kategori 1'i sabit ID ile eliyordu.
  // Artık kategori dokümanındaki açık `selectableInRecipe` bayrağı karar verir.
  const selectableCategories = categories.filter((c) => c.selectableInRecipe);

  const options = useMemo(() => {
    return components.filter((c) => {
      if (categoryId && c.categoryId !== categoryId) return false;
      // Eski kural: adet birimli koliler reçete bileşeni listesinde gizlenir
      // (onlar Paket Bilgileri ekranından eklenir). Artık açık bayrakla.
      if (c.isSelectableAsComponent === false) return false;
      // ★ Eski kuralın KENDİSİ de burada: ambalaj + adet birimi = KOLİ. AMBALAJ
      // kategorisi sargı (Z-KARTON, POŞET — m²) için seçiciye açıldı; bayrağı
      // unutulmuş bir koli reçeteye girerse hem maliyete hem desiye iki kez
      // yazılırdı. Parça kartından yeni koli açmak bu bayrağı varsayılan "açık"
      // bırakıyor, bu yüzden bayrağa tek başına güvenilmiyor.
      if (c.isPackaging === true && c.unit === "adet") return false;
      return true;
    });
  }, [components, categoryId]);

  const selected = components.find((c) => c.id === componentId);

  async function submit() {
    setLocalError(null);
    if (!componentId) {
      setLocalError("Bileşen seçilmelidir.");
      return;
    }
    const q = parseDecimal(quantity);
    if (q === null) {
      setLocalError('Miktar okunamadı. Ondalık için virgül kullanın (örnek: 0,5).');
      return;
    }
    if (q <= 0) {
      setLocalError("Miktar sıfırdan büyük olmalıdır.");
      return;
    }

    const res = await save.run({
      productId,
      ...(line ? { lineId: line.id } : {}),
      type: "component",
      componentId,
      quantity: q,
      explanation: explanation.trim(),
      order: line?.order ?? nextOrder,
    });
    if (res) onSaved();
  }

  return (
    <Modal open onClose={onClose} title={line ? "Reçete satırını düzenle" : "Reçeteye bileşen ekle"}>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Kategori">
          <select
            className={INPUT}
            value={categoryId}
            onChange={(e) => {
              setCategoryId(e.target.value);
              setComponentId("");
            }}
          >
            <option value="">Tüm kategoriler</option>
            {selectableCategories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Parça">
          <select
            className={INPUT}
            value={componentId}
            onChange={(e) => setComponentId(e.target.value)}
          >
            <option value="">Seçiniz…</option>
            {options.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Miktar (1 adet mamul için)" hint="Ondalık için virgül: 0,5">
          <DecimalInput value={quantity} onChange={setQuantity} />
        </Field>

        <Field label="Birim" hint="Parçadan gelir, ayrıca kaydedilmez.">
          <input className={INPUT} value={selected?.unit ?? ""} readOnly />
        </Field>

        <div className="md:col-span-2">
          <Field label="Açıklama (isteğe bağlı)">
            <input
              className={INPUT}
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              maxLength={500}
            />
          </Field>
        </div>
      </div>

      <p className="mt-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
        Bileşenin alış fiyatı bu satıra <strong>kopyalanmaz</strong>. Maliyet, fiyat değiştiğinde
        sunucu tarafında yeniden hesaplanır.
      </p>

      <ErrorNote error={localError ?? save.error} />

      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="rounded border border-slate-300 px-4 py-2 text-sm">
          Vazgeç
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={save.loading}
          className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
        >
          {save.loading ? "Kaydediliyor…" : "Kaydet"}
        </button>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// AYAK LİSTESİ — type: "leg"
// Ne maliyete ne stoğa girer; yalnızca üretim talimatıdır.
// Eski modelde `component_product_id = 0` yazılıp INNER JOIN yan etkisiyle koli
// listesinden eleniyordu — kırılgan kural. Artık açık `type` alanı.
// ═══════════════════════════════════════════════════════════════════════════

export function LegBlock({ productId }: { productId: string }) {
  const can = useCan();
  const toast = useToast();
  const readOnly = !can.isStaff;

  const { data: lines, loading, error } = useCollection<RecipeLine>(legsPath(productId), [
    where("deleted", "==", false),
    orderBy("order"),
  ]);

  const save = useCallableAction<SaveRecipeLineInput, SaveRecipeLineOutput>("saveRecipeLine");
  const remove = useCallableAction<DeleteRecipeLineInput, { ok: boolean }>("deleteRecipeLine");

  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  async function add() {
    setLocalError(null);
    if (!description.trim()) {
      setLocalError("Ayak tarifi boş olamaz.");
      return;
    }
    const q = parseDecimal(quantity);
    if (q === null || q <= 0) {
      setLocalError("Adet sıfırdan büyük bir sayı olmalıdır.");
      return;
    }
    const res = await save.run({
      productId,
      type: "leg",
      componentId: "",
      quantity: q,
      explanation: description.trim(),
      order: (lines[lines.length - 1]?.order ?? 0) + 10,
    });
    if (res) {
      toast.success("Ayak tarifi eklendi.");
      setDescription("");
      setQuantity("");
    }
  }

  return (
    <section>
      <header className="mb-3">
        <h3 className="text-base font-semibold text-slate-800">Ayak Listesi</h3>
        <p className="text-xs text-slate-500">
          Üretime iletilen serbest metin tarif. Maliyete girmez, stoktan düşmez.
        </p>
      </header>

      {!readOnly ? (
        <div className="mb-3 grid grid-cols-1 gap-2 rounded border border-slate-200 bg-slate-50 p-3 md:grid-cols-[1fr_120px_auto] md:items-end">
          <Field label="Ayak Tarifi">
            <input
              className={INPUT}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Örn. 15 cm ahşap konik ayak, ceviz"
              maxLength={500}
            />
          </Field>
          <Field label="Adet">
            <DecimalInput value={quantity} onChange={setQuantity} />
          </Field>
          <button
            type="button"
            onClick={add}
            disabled={save.loading}
            className="h-[34px] rounded-lg bg-gray-900 px-4 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
          >
            Ekle
          </button>
        </div>
      ) : null}

      <ErrorNote error={localError ?? save.error ?? remove.error ?? error} />
      {loading ? <Spinner /> : null}

      {!loading && lines.length === 0 ? (
        <EmptyState title="Ayak tarifi yok" description="Bu ürün için ayak talimatı girilmemiş." />
      ) : null}

      {lines.length > 0 ? (
        <div className="overflow-x-auto rounded border border-slate-200">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-3 py-2">Ayak Tarifi</th>
                <th className="px-3 py-2 text-right">Adet</th>
                {!readOnly ? <th className="px-3 py-2 text-right">İşlem</th> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {lines.map((l) => (
                <tr key={l.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 text-slate-800">{l.explanation}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{n(l.quantity)}</td>
                  {!readOnly ? (
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                        onClick={() => void remove.run({ productId, lineId: l.id })}
                      >
                        Çıkar
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
